import React from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { Button, FloatLabel, SCIcon, Controller, SubmitHandler, DDCountries, InputNumber, } from "../../Common/Imports";
import { ConfigService_EXT } from '../../Models/Classes';
import { DDDaysOfWeek } from '../../Components/Dropdowns/DDDaysOfWeek';
import { DDServiceDateType } from '../../Components/Dropdowns/DDServiceDateType';

interface ServiceConfigsNewProps {
    stakeholderId: number;
    OnSave?: () => void;
}

const ServiceConfigsNew: React.FC<ServiceConfigsNewProps> = ({ stakeholderId, OnSave }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const {
        control,
        handleSubmit,
    } = useForm<ConfigService_EXT>();
    const AxiosHelper = useAxios();

    const onSubmit: SubmitHandler<ConfigService_EXT> = async (data: ConfigService_EXT) => {
        try {
            data.idStakeholder = stakeholderId;
            await AxiosHelper.post(`StakeholderConfigs/Services/CreateConfig`, data);
            showToast('success', 'Success', t("Common.Toasts.New.Success"));
            if (OnSave) {
                OnSave();
            }
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <div className="row mb-3 mt-3">
                {/* Country Dropdown */}
                <Controller
                    name="idCountry"
                    control={control}
                    rules={{ required: `${t("Stakeholder.Configs_Service.Country")} ${t("Common.Form.Required")}` }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-12 mb-4">
                            <FloatLabel>
                                <DDCountries
                                    value={field.value}
                                    onChange={(value) => field.onChange(value)}
                                    onBlur={field.onBlur}
                                />
                                <label htmlFor="country">{t("Stakeholder.Configs_Service.Country")}</label>
                            </FloatLabel>
                            {fieldState.invalid && (
                                <small className="p-error">
                                    {fieldState.error?.message}
                                </small>
                            )}
                        </div>
                    )}
                />
                <Controller
                    name="sendTentativeNumber"
                    control={control}
                    rules={{ required: t("Stakeholder.Configs_Service.TentativeNumber") + " " + t("Auth.Register.Form.Required") }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-12 mb-4">
                            <FloatLabel>
                                <InputNumber
                                    id="sendTentativeNumber"
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.value)}
                                    onBlur={field.onBlur}
                                    className={classNames({ 'p-invalid': fieldState.invalid })}
                                />
                                <label htmlFor="sendTentativeNumber">{t("Stakeholder.Configs_Service.TentativeNumber")}</label>
                            </FloatLabel>
                            {fieldState.invalid && (
                                <small className="p-error">
                                    {fieldState.error?.message}
                                </small>
                            )}
                        </div>
                    )}
                />
                <Controller
                    name="hoursBefore"
                    control={control}
                    rules={{ required: t("Stakeholder.Configs_Service.HoursBefore") + " " + t("Auth.Register.Form.Required") }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-12 mb-4">
                            <FloatLabel>
                                <InputNumber
                                    id="hoursBefore"
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.value)}
                                    onBlur={field.onBlur}
                                    className={classNames({ 'p-invalid': fieldState.invalid })}
                                />
                                <label htmlFor="hoursBefore">{t("Stakeholder.Configs_Service.HoursBefore")}</label>
                            </FloatLabel>
                            {fieldState.invalid && (
                                <small className="p-error">
                                    {fieldState.error?.message}
                                </small>
                            )}
                        </div>
                    )}
                />
                <Controller
                    name="dayOfWeek"
                    control={control}
                    rules={{ required: `${t("Stakeholder.Configs_Service.DayOfWeek")} ${t("Common.Form.Required")}` }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-12 mb-4">
                            <FloatLabel>
                                <DDDaysOfWeek
                                    value={field.value}
                                    onSelect={(value) => field.onChange(value)}
                                />
                                <label htmlFor="country">{t("Stakeholder.Configs_Service.DayOfWeek")}</label>
                            </FloatLabel>
                            {fieldState.invalid && (
                                <small className="p-error">
                                    {fieldState.error?.message}
                                </small>
                            )}
                        </div>
                    )}
                />
                <Controller
                    name="idType"
                    control={control}
                    rules={{ required: `${t("Stakeholder.Configs_Service.DayOfWeek")} ${t("Common.Form.Required")}` }}
                    render={({ field, fieldState }) => (
                        <div className="col-md-12 mb-4">
                            <FloatLabel>
                                <DDServiceDateType
                                    value={field.value}
                                    onSelect={(value) => field.onChange(value)}
                                />
                                <label htmlFor="country">{t("Stakeholder.Configs_Service.Type")}</label>
                            </FloatLabel>
                            {fieldState.invalid && (
                                <small className="p-error">
                                    {fieldState.error?.message}
                                </small>
                            )}
                        </div>
                    )}
                />
            </div>
            <div className="row">
                <div className="col">
                    <Button type="submit" label={t("Common.Buttons.Save")} icon={<SCIcon Name="Save" />} />
                </div>
            </div>
        </form>
    );
};

export default ServiceConfigsNew;
